import React, { Dispatch, SetStateAction } from 'react';
import { OrderedTest } from '@/hooks/use-test-order';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash, Minus, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';

export interface OrderSummaryProps {
  tests: OrderedTest[];
  onRemove: (testId: string) => void;
  onUpdateQuantity: (testId: string, quantity: number) => void;
  onClear: () => void;
  notes: string;
  onNotesChange: Dispatch<SetStateAction<string>>;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  tests,
  onRemove,
  onUpdateQuantity,
  onClear,
  notes,
  onNotesChange,
}) => {
  const isEmpty = tests.length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-md font-semibold">Order Summary</CardTitle>
        <Badge variant="outline">{tests.length} {tests.length === 1 ? 'test' : 'tests'}</Badge>
      </CardHeader>
      <CardContent>
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <p className="text-muted-foreground mb-2">Your order is empty</p>
            <p className="text-xs text-muted-foreground">Add tests from the selection panel</p>
          </div>
        ) : (
          <ScrollArea className="h-[250px] pr-4">
            <ul className="space-y-2">
              {tests.map((test) => (
                <li key={test.testId} className="flex items-center justify-between p-2 border rounded-md">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{test.name}</p>
                    {test.category && (
                      <Badge variant="secondary" className="mt-1 text-xs">{test.category}</Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => onUpdateQuantity(test.testId, test.quantity - 1)}
                      disabled={test.quantity <= 1}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-4 text-center">{test.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => onUpdateQuantity(test.testId, test.quantity + 1)}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive hover:text-destructive"
                      onClick={() => onRemove(test.testId)}
                    >
                      <Trash className="h-3 w-3" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </ScrollArea>
        )}

        <div className="mt-4">
          <label htmlFor="order-notes" className="block text-sm font-medium mb-2">
            Notes for your provider
          </label>
          <Textarea
            id="order-notes"
            placeholder="Add any special instructions or information about your order..."
            className="resize-none"
            rows={3}
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onNotesChange(e.target.value)}
          />
        </div>
      </CardContent>
      
      {!isEmpty && (
        <CardFooter>
          <Button variant="outline" size="sm" onClick={onClear} className="w-full">
            <Trash className="h-4 w-4 mr-2" />
            Clear Order
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default OrderSummary; 